import React, { useState, useEffect } from 'react';
import { useProject } from '../context/ProjectContext.tsx';
import { useReviewer } from '../context/ReviewerContext.tsx';
import { literatureService } from '../services/literatureService.ts';
import { Literature, Reviewer } from '../types';
import '../styles/FillDOIs.css';
import { reviewService } from '../services/reviewService.ts';

// Define interfaces for duplicate finding
interface DuplicateItem extends Literature {
    similarity?: number;
}

interface DuplicateGroup {
    original: DuplicateItem;
    duplicates: DuplicateItem[];
}

interface DuplicatesResponse {
    duplicate_groups: DuplicateGroup[];
    total_groups: number;
}

export const FillDOIs: React.FC = () => {
    const { currentProject } = useProject();
    const { currentReviewer } = useReviewer();
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [lookingUpDOIs, setLookingUpDOIs] = useState(false);
    const [findingDuplicates, setFindingDuplicates] = useState(false);
    const [literatureWithoutDOIs, setLiteratureWithoutDOIs] = useState<Literature[]>([]);
    const [potentialMatches, setPotentialMatches] = useState<Record<number, Literature[]>>({});
    const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
    const [showDuplicates, setShowDuplicates] = useState(false);
    const [message, setMessage] = useState('');
    const [selectedReviewerId, setSelectedReviewerId] = useState<number | null>(null);
    const [selectedDecision, setSelectedDecision] = useState<string>('ACCEPTED');

    useEffect(() => {
        if (currentProject) {
            fetchLiteratureWithoutDOIs();
        }
    }, [currentProject, selectedReviewerId, selectedDecision]);

    const fetchLiteratureWithoutDOIs = async () => {
        if (!currentProject) return;

        setLoading(true);
        try {
            const filter = {
                project_id: currentProject.id,
                reviewer_decision_filters: selectedReviewerId
                    ? [
                          {
                              reviewer_id: selectedReviewerId,
                              decision: selectedDecision,
                              decision_operator: 'equals',
                          },
                      ]
                    : [],
            };

            const response = await literatureService.getLiteratureWithoutDOIs(filter);
            setLiteratureWithoutDOIs(response.items);
        } catch (error) {
            console.error('Error fetching literature without DOIs:', error);
            setMessage('Failed to fetch literature without DOIs');
        } finally {
            setLoading(false);
        }
    };

    const findPotentialDOIMatches = async () => {
        if (!currentProject || literatureWithoutDOIs.length === 0) return;

        setSearching(true);
        setMessage('Searching for potential DOI matches...');

        try {
            const matches = await literatureService.findPotentialDOIMatches(
                currentProject.id,
                literatureWithoutDOIs.map((lit) => lit.id)
            );
            setPotentialMatches(matches);
            setMessage(
                Object.keys(matches).length > 0
                    ? 'Potential matches found. Please review them below.'
                    : 'No potential matches found.'
            );
        } catch (error) {
            console.error('Error finding potential DOI matches:', error);
            setMessage('Failed to find potential DOI matches');
        } finally {
            setSearching(false);
        }
    };

    const acceptMatch = async (literatureId: number, matchedLiterature: Literature) => {
        try {
            await literatureService.updateLiterature(literatureId, {
                doi: matchedLiterature.doi,
            });

            // Update the local state
            setLiteratureWithoutDOIs((prev) => prev.filter((lit) => lit.id !== literatureId));
            setPotentialMatches((prev) => {
                const updated = { ...prev };
                delete updated[literatureId];
                return updated;
            });

            setMessage(`DOI updated successfully for "${matchedLiterature.title}"`);
        } catch (error) {
            console.error('Error accepting match:', error);
            setMessage('Failed to update DOI');
        }
    };

    const generateFakeDOI = async (literatureId: number) => {
        try {
            const fakeDOI = `fake-doi-${Date.now()}-${literatureId}`;
            await literatureService.updateLiterature(literatureId, {
                doi: fakeDOI,
            });

            // Update the local state
            setLiteratureWithoutDOIs((prev) => prev.filter((lit) => lit.id !== literatureId));
            setPotentialMatches((prev) => {
                const updated = { ...prev };
                delete updated[literatureId];
                return updated;
            });

            setMessage(`Fake DOI generated successfully`);
        } catch (error) {
            console.error('Error generating fake DOI:', error);
            setMessage('Failed to generate fake DOI');
        }
    };

    // New function to lookup DOIs for all literature items
    const lookupAllDOIsExternally = async () => {
        if (literatureWithoutDOIs.length === 0) return;

        setLookingUpDOIs(true);
        setMessage('Looking up DOIs for all literature entries. This may take a while...');

        try {
            let foundCount = 0;

            // Process each literature item sequentially to avoid overwhelming the API
            for (const lit of literatureWithoutDOIs) {
                const result = await literatureService.lookupDOIByTitle(lit.title, lit.authors);

                if (result.found && result.doi) {
                    // Create a match object from the lookup result
                    const externalMatch: Literature = {
                        // ...lit,
                        ...result,
                        id: -1 * (Date.now() + Math.floor(Math.random() * 1000)), // Use a negative ID with some randomness
                        doi: result.doi,
                        title: `[EXTERNAL MATCH] ${result.title}`,
                    };

                    // Add to potential matches
                    setPotentialMatches((prev) => {
                        const updated = { ...prev };
                        if (!updated[lit.id]) {
                            updated[lit.id] = [];
                        }
                        updated[lit.id] = [...updated[lit.id], externalMatch];
                        return updated;
                    });

                    foundCount++;
                    setMessage(`Found ${foundCount} DOIs so far. Processing...`);
                }
            }

            setMessage(`Completed DOI lookup. Found potential matches for ${foundCount} literature entries.`);
        } catch (error) {
            console.error('Error looking up DOIs for all items:', error);
            setMessage('Failed to complete DOI lookup for all items');
        } finally {
            setLookingUpDOIs(false);
        }
    };

    const updateManualDOI = async (literatureId: number, manualDOI: string) => {
        try {
            await literatureService.updateLiterature(literatureId, {
                doi: manualDOI,
            });

            // Update the local state
            setLiteratureWithoutDOIs((prev) => prev.filter((lit) => lit.id !== literatureId));
            setPotentialMatches((prev) => {
                const updated = { ...prev };
                delete updated[literatureId];
                return updated;
            });

            setMessage(`DOI manually updated successfully`);
        } catch (error) {
            console.error('Error updating DOI:', error);
            setMessage('Failed to update DOI');
        }
    };

    const updateManualURL = async (literatureId: number, url: string) => {
        try {
            // Note: 'url' is not in the LiteratureUpdateData type, but we're using it anyway
            // You might want to update the type definition if this is a valid field
            await literatureService.updateLiterature(literatureId, {
                url: url,
            } as any); // Using 'as any' to bypass TypeScript checking
            setMessage(`URL updated successfully`);
        } catch (error) {
            console.error('Error updating URL:', error);
            setMessage('Failed to update URL');
        }
    };

    // Function to find duplicate entries
    const findDuplicates = async () => {
        if (!currentProject) return;

        setFindingDuplicates(true);
        setShowDuplicates(true);
        setMessage('Searching for duplicate entries...');

        try {
            const result = await literatureService.findDuplicates(currentProject.id);
            setDuplicateGroups(result.duplicate_groups);

            if (result.total_groups > 0) {
                setMessage(`Found ${result.total_groups} groups of potential duplicates.`);
            } else {
                setMessage('No duplicate entries found.');
            }
        } catch (error) {
            console.error('Error finding duplicates:', error);
            setMessage('Failed to find duplicate entries');
        } finally {
            setFindingDuplicates(false);
        }
    };

    // Function to merge duplicate entries
    const mergeDuplicates = async (originalId: number, duplicateId: number) => {
        try {
            // Get the original and duplicate items
            const group = duplicateGroups.find((g) => g.original.id === originalId);
            if (!group) return;

            const duplicate = group.duplicates.find((d) => d.id === duplicateId);
            if (!duplicate) return;

            // If the original doesn't have a DOI but the duplicate does, copy the DOI
            if ((!group.original.doi || group.original.doi === '') && duplicate.doi) {
                await literatureService.updateLiterature(originalId, {
                    doi: duplicate.doi,
                });
                setMessage(`DOI copied from duplicate to original.`);
            }

            // If the original doesn't have full text but the duplicate does, mark it as available
            if (!group.original.full_text_available && duplicate.full_text_available) {
                await literatureService.updateLiterature(originalId, {
                    full_text_available: true,
                });
                setMessage(`Full text availability updated.`);
            }

            // Remove the duplicate from the UI
            setDuplicateGroups((prevGroups) =>
                prevGroups
                    .map((g) => {
                        if (g.original.id === originalId) {
                            return {
                                ...g,
                                duplicates: g.duplicates.filter((d) => d.id !== duplicateId),
                            };
                        }
                        return g;
                    })
                    .filter((g) => g.duplicates.length > 0)
            );

            setMessage(`Entries merged successfully.`);
        } catch (error) {
            console.error('Error merging duplicates:', error);
            setMessage('Failed to merge entries');
        }
    };

    return (
        <div className="fill-dois-container">
            <div className="fill-dois-explanation">
                <h2>Fill Missing DOIs</h2>
                <p>
                    DOIs (Digital Object Identifiers) are used to retrieve full-text documents across projects.
                    Without a DOI, PDFs cannot be uploaded or associated with literature entries.
                </p>
                <p>
                    This tool helps you find potential DOI matches for literature entries that are missing DOIs
                    by comparing titles and authors with other literature entries across all projects.
                </p>
            </div>

            <div className="fill-dois-filters">
                <h3>Filter by Reviewer Decision</h3>
                <div className="filter-controls">
                    <div className="filter-group">
                        <label htmlFor="reviewer-select">Reviewer:</label>
                        <select
                            id="reviewer-select"
                            value={selectedReviewerId || ''}
                            onChange={(e) =>
                                setSelectedReviewerId(e.target.value ? parseInt(e.target.value) : null)
                            }>
                            <option value="">All Reviewers</option>
                            {currentReviewer?.map((reviewer) => (
                                <option key={reviewer.id} value={reviewer.id}>
                                    {reviewer.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="decision-select">Decision:</label>
                        <select
                            id="decision-select"
                            value={selectedDecision}
                            onChange={(e) => setSelectedDecision(e.target.value)}
                            disabled={!selectedReviewerId}>
                            <option value="ACCEPTED">Accepted</option>
                            <option value="NEEDS_FULL_TEXT">Requires Full Text</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="fill-dois-stats">
                <h3>Statistics</h3>
                <p>
                    Number of literature entries without DOIs: <strong>{literatureWithoutDOIs.length}</strong>
                </p>
            </div>

            {message && <div className="fill-dois-message">{message}</div>}

            <div className="fill-dois-actions">
                <button
                    onClick={findPotentialDOIMatches}
                    disabled={searching || literatureWithoutDOIs.length === 0}
                    className="primary-button">
                    {searching ? 'Searching...' : 'Find Potential DOI Matches'}
                </button>
                <button
                    onClick={lookupAllDOIsExternally}
                    disabled={lookingUpDOIs || literatureWithoutDOIs.length === 0}
                    className="primary-button">
                    {lookingUpDOIs ? 'Looking up DOIs...' : 'Lookup DOI for All Items'}
                </button>
                <button onClick={findDuplicates} disabled={findingDuplicates} className="primary-button">
                    {findingDuplicates ? 'Finding Duplicates...' : 'Find Duplicate Entries'}
                </button>
                <button onClick={fetchLiteratureWithoutDOIs} disabled={loading} className="secondary-button">
                    {loading ? 'Refreshing...' : 'Refresh List'}
                </button>
                <button
                    onClick={() => setShowDuplicates(!showDuplicates)}
                    className="secondary-button"
                    disabled={duplicateGroups.length === 0}>
                    {showDuplicates ? 'Hide Duplicates' : 'Show Duplicates'}
                </button>
            </div>

            {showDuplicates && duplicateGroups.length > 0 && (
                <div className="duplicate-groups">
                    <h3>Potential Duplicate Entries</h3>
                    <p className="duplicate-explanation">
                        These are potential duplicate entries found in your project. You can merge information
                        from duplicates to the original entry.
                    </p>

                    {duplicateGroups.map((group) => (
                        <div key={group.original.id} className="duplicate-group">
                            <div className="original-item">
                                <h4>Original Entry</h4>
                                <div className="literature-details">
                                    <h5>{group.original.title}</h5>
                                    <p>
                                        <strong>Authors:</strong> {group.original.authors}
                                    </p>
                                    <p>
                                        <strong>Journal:</strong> {group.original.journal}
                                    </p>
                                    <p>
                                        <strong>Year:</strong> {group.original.year}
                                    </p>
                                    <p>
                                        <strong>DOI:</strong> {group.original.doi || 'None'}
                                    </p>
                                    <p>
                                        <strong>Full Text Available:</strong>{' '}
                                        {group.original.full_text_available ? 'Yes' : 'No'}
                                    </p>
                                </div>
                            </div>

                            <div className="duplicate-items">
                                <h4>Potential Duplicates</h4>
                                {group.duplicates.map((duplicate) => (
                                    <div key={duplicate.id} className="duplicate-item">
                                        <div className="literature-details">
                                            <h5>{duplicate.title}</h5>
                                            <p>
                                                <strong>Authors:</strong> {duplicate.authors}
                                            </p>
                                            <p>
                                                <strong>Journal:</strong> {duplicate.journal}
                                            </p>
                                            <p>
                                                <strong>Year:</strong> {duplicate.year}
                                            </p>
                                            <p>
                                                <strong>DOI:</strong> {duplicate.doi || 'None'}
                                            </p>
                                            <p>
                                                <strong>Full Text Available:</strong>{' '}
                                                {duplicate.full_text_available ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                <strong>Similarity Score:</strong>{' '}
                                                {duplicate.similarity
                                                    ? Math.round(duplicate.similarity * 100)
                                                    : 0}
                                                %
                                            </p>
                                        </div>
                                        <div className="duplicate-actions">
                                            <button
                                                onClick={() => mergeDuplicates(group.original.id, duplicate.id)}
                                                className="merge-button">
                                                Merge with Original
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="fill-dois-list">
                <h3>Literature Entries Without DOIs</h3>
                {literatureWithoutDOIs.length === 0 ? (
                    <p>All literature entries have DOIs or no entries match the current filter criteria.</p>
                ) : (
                    <div className="literature-without-dois">
                        {literatureWithoutDOIs.map((lit) => (
                            <div key={lit.id} className="literature-item">
                                <div className="literature-details">
                                    <h4>{lit.title}</h4>
                                    <p>
                                        <strong>Authors:</strong> {lit.authors}
                                    </p>
                                    <p>
                                        <strong>Journal:</strong> {lit.journal}
                                    </p>
                                    <p>
                                        <strong>Year:</strong> {lit.year}
                                    </p>
                                    <div className="external-search">
                                        <a
                                            href={`https://scholar.google.com/scholar?q="${encodeURIComponent(
                                                lit.title
                                            )}"`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="google-scholar-link">
                                            Search in Google Scholar
                                        </a>
                                        <a
                                            href={`https://www.google.com/search?q="${encodeURIComponent(
                                                lit.title
                                            )}"`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="google-link">
                                            Search in Google
                                        </a>
                                    </div>
                                    <div className="manual-input-container">
                                        <div className="manual-doi-input">
                                            <input
                                                type="text"
                                                placeholder="Enter DOI manually"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        const manualDOI = e.currentTarget.value.trim();
                                                        if (manualDOI) {
                                                            updateManualDOI(lit.id, manualDOI);
                                                        }
                                                    }
                                                }}
                                                className="doi-input"
                                            />
                                            <small className="doi-hint">Press Enter to save DOI</small>
                                        </div>
                                        <div className="manual-url-input">
                                            <input
                                                type="text"
                                                placeholder="Enter paper URL"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        const url = e.currentTarget.value.trim();
                                                        if (url) {
                                                            updateManualURL(lit.id, url);
                                                        }
                                                    }
                                                }}
                                                className="url-input"
                                            />
                                            <small className="url-hint">Press Enter to save URL</small>
                                        </div>
                                    </div>
                                </div>

                                {potentialMatches[lit.id] ? (
                                    <div className="potential-matches">
                                        <h5>Potential Matches:</h5>
                                        {potentialMatches[lit.id].map((match) => (
                                            <div key={match.id} className="match-item">
                                                <div className="match-details">
                                                    <p>
                                                        <strong>Title:</strong> {match.title}
                                                    </p>
                                                    <p>
                                                        <strong>Authors:</strong> {match.authors}
                                                    </p>
                                                    <p>
                                                        <strong>DOI:</strong>{' '}
                                                        <a href={reviewService.getDoiUrl(match.doi)}>
                                                            {match.doi}
                                                        </a>
                                                    </p>
                                                </div>
                                                <div className="match-actions">
                                                    <button
                                                        onClick={() => acceptMatch(lit.id, match)}
                                                        className="accept-button">
                                                        Accept Match
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-matches">
                                        {potentialMatches[lit.id]
                                            ? 'No matches found'
                                            : 'Click "Find Potential DOI Matches" to search'}
                                    </div>
                                )}

                                <div className="literature-actions">
                                    <button onClick={() => generateFakeDOI(lit.id)} className="fake-doi-button">
                                        Generate Fake DOI
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
